/**
 * Login route — /login
 *
 * Shows the LoginShell from the ACC UI package and wires the "Continue
 * with Microsoft" button to MSAL's loginRedirect. Also, if the user
 * arrives here already authenticated (e.g. bookmark), sends them to the
 * dashboard.
 */

import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { LoginShell } from '@/acc-ui/components/LoginShell';
import { beginLoginRedirect } from '@/auth/msal';
import { useAuthStore } from '@/auth/auth-store';

interface LocationState {
  from?: string;
}

export function LoginPage() {
  const status = useAuthStore((s) => s.status);
  const fetchMe = useAuthStore((s) => s.fetchMe);
  const location = useLocation();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (status === 'unknown') void fetchMe();
  }, [status, fetchMe]);

  if (status === 'authed') {
    const state = location.state as LocationState | null;
    const target = state?.from ?? '/';
    return <Navigate to={target} replace />;
  }

  async function onSignIn() {
    setIsRedirecting(true);
    setError(null);
    try {
      await beginLoginRedirect();
      // The browser navigates to Microsoft — this line typically
      // does not execute.
    } catch (err) {
      setIsRedirecting(false);
      setError(
        err instanceof Error && err.message
          ? err.message
          : 'Could not start Microsoft sign-in. Please try again.',
      );
    }
  }

  return (
    <LoginShell
      onMicrosoftLogin={onSignIn}
      isLoading={isRedirecting}
      errorMessage={error}
    />
  );
}
